import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { NotificationDatePipe } from '@interacta-shared/data-access-notification';
import { NotificationPanelService } from '@interacta-shared/feature-notification';
import { AvatarComponent } from '@interacta-shared/ui';
import { IconComponent } from '@interacta-shared/ui-icons';
import { DateTooltipPipe } from '@interacta-shared/util-common';
import { isEventPost } from '@interacta/util-post';
import { UserNotificationEvent } from '@modules/notification/models/user-notification.model';
import { buildNotificationText } from '@modules/notification/utils/user-notification.util';
import { CurrentLanguagePipe } from '@modules/shared-v2/pipes/current-language.pipe';

import { ZonedDateComponent } from '@modules/shared-v2/components/zoned-date/zoned-date.component';

import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'interacta-user-notification',
    imports: [
        AvatarComponent,
        RouterLink,
        NgClass,
        MatTooltip,
        CurrentLanguagePipe,
        NotificationDatePipe,
        DateTooltipPipe,
        IconComponent,
        ZonedDateComponent,
    ],
    templateUrl: './user-notification.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserNotificationComponent {
    readonly notificationPanelService = inject(NotificationPanelService);
    readonly translateService = inject(TranslateService);

    readonly notification = input.required<UserNotificationEvent>();

    readonly text = computed(() =>
        buildNotificationText(this.notification(), this.translateService),
    );

    readonly authors = computed(() => this.notification().authorUsers);
    readonly hasMultipleAuthors = computed(() => this.authors().length > 1);
    readonly firstAuthor = computed(() =>
        this.authors().length ? this.authors()[0] : null,
    );

    readonly read = computed(() => this.notification().read);

    readonly routerLink = computed(() => this.notification().routerLink);
    readonly tabindex = computed(() =>
        this.routerLink() != null ? undefined : 0,
    );

    readonly eventData = computed(() => {
        const post = this.notification().post;
        if (post && isEventPost(post)) {
            return {
                allDay: post.allDay,
                startAt:
                    post.startAt?.zonedDatetime ??
                    post.startAt?.localDatetime ??
                    null,
            };
        }
        return null;
    });
}
